import { createContext, useContext, type ReactNode } from 'react';
import type {
  BoardRealtime,
  BoardStore,
  FeedProvenanceStore,
  ChannelReadStore,
  ChannelRealtime,
  RegisterStore,
  RosterStore,
  TimesheetStore,
  TrustedPersonStore,
} from './ports.js';
import type { BoardDemandService, BoardPresenceService } from './boardPresenceService.js';
import type {
  ActivityCatalogService,
  MetricRegistryService,
  StaffingConfigService,
  ChannelCurationService,
  RegisterService,
  ShiftRequestDecisionService,
  ShiftRequestService,
  TimesheetCorrectionService,
  TrustedPersonService,
  DeskListService,
} from './adminServices.js';

/**
 * `@lemontaigu/staff/react` — the React binding for C3's client-side read ports
 * (ladder step 6).
 *
 * WHY IT LIVES HERE. Three SPAs (reception, staff, admin) read the same three
 * C3 aggregates. Without this, each grows its own `createContext` +
 * `useBoard()` trio — the `AuthGate` duplication (F8) starting over, and three
 * subtly different hooks named the same thing.
 *
 * It is NOT in `packages/platform-ui`: that package's contract is "knows no
 * domain", and these are C3's ports. It is not in an app either, because no app
 * owns it. It belongs next to the ports it exposes.
 *
 * WHAT IT MUST NOT DO. This file names no infrastructure — it takes ports and
 * hands them back. Every concrete `Supabase*Store` is built by the caller's
 * composition root (a `*Provider.tsx` under an app's src), which is the only
 * place allowed to import the adapters subpath. That is what keeps the SPAs
 * testable: a test renders a view inside this provider over the fakes.
 *
 * Reads only, matching the ports. The write path is ladder step 7.
 */

export interface StaffStores {
  board: BoardStore;
  /** When the board's figures were last true (M4). Optional: an app that does
   *  not render the demand grid has no use for it, and a missing provenance
   *  must degrade to "unknown", never to a confident timestamp. */
  feedProvenance?: FeedProvenanceStore;
  roster: RosterStore;
  timesheet: TimesheetStore;
  /** Operator-only (`is_admin`) — provided by the back-office root, nowhere else. */
  register: RegisterStore;
  /** The staff channel's READ side; the write side is server-only. */
  channel: ChannelReadStore;
  /**
   * DRIVING port (step 7) — the board's presence/absence use cases. A view
   * calls this instead of composing writes itself; that composition is what
   * F14 was.
   */
  boardPresence: BoardPresenceService;
  /** DRIVING port (step 7) — the demand grid and per-day activity overrides. */
  boardDemand: BoardDemandService;
  /** Realtime (F13) — subscriptions, not queries; see ports.ts. */
  boardRealtime: BoardRealtime;
  channelRealtime: ChannelRealtime;
  /** Operator-only driving ports — bound by the back-office root, nowhere else. */
  activityCatalog: ActivityCatalogService;
  /**
   * Operator-only for its WRITES (every one gated on `rh.board.manage`), but
   * `load()` is ungated and its RLS is `rh.board.read` — so reception also
   * binds it, read-only, for the « prévisionnel » strip (2026-08-01). The
   * staffing EDITOR remains back-office-only.
   */
  staffingConfig: StaffingConfigService;
  /**
   * The metric registry (ADR-0015). Like `staffingConfig`, its `load()` is
   * ungated (RLS: `rh.board.read`) so any surface rendering a badge can resolve
   * it; the EDITOR is back-office-only.
   */
  metricRegistry: MetricRegistryService;
  timesheetCorrection: TimesheetCorrectionService;
  registerWrite: RegisterService;
  channelCuration: ChannelCurationService;
  /** The employee's side of a shift request — identity-reachable, ungated. Bound by the staff app. */
  shiftRequests: ShiftRequestService;
  /** The operator's inbox — gated on `rh.timesheet.write`. Bound by the back-office. */
  shiftRequestDecisions: ShiftRequestDecisionService;
  /** « Hors Staff » reads (ADR-0024) — the only path to those people and their punches. Back-office. */
  trustedPersons: TrustedPersonStore;
  /** « Hors Staff » writes — gated on `rh.register.write` / `rh.timesheet.write`. Back-office. */
  trustedPersonService: TrustedPersonService;
  /** The desk list (ADR-0026) — who « Qui êtes-vous ? » shows. Back-office. */
  deskList: DeskListService;
}

const StaffStoresContext = createContext<Partial<StaffStores> | null>(null);

/**
 * Binds C3's read ports for a subtree. `stores` is `Partial` on purpose: an app
 * that only shows the board should not have to construct a timesheet adapter to
 * satisfy a type, and asking for a port that was not provided fails loudly at
 * the point of use rather than silently returning undefined.
 */
export function StaffStoresProvider({
  stores,
  children,
}: {
  stores: Partial<StaffStores>;
  children: ReactNode;
}) {
  return <StaffStoresContext.Provider value={stores}>{children}</StaffStoresContext.Provider>;
}

function usePort<K extends keyof StaffStores>(key: K): StaffStores[K] {
  const ctx = useContext(StaffStoresContext);
  if (!ctx) throw new Error(`use${key} must be used within <StaffStoresProvider>`);
  const port = ctx[key];
  if (!port) throw new Error(`<StaffStoresProvider> was not given a "${key}" store`);
  return port;
}

export const useBoard = (): BoardStore => usePort('board');
/** Undefined where the root did not bind one — the views render « inconnu ». */
export const useFeedProvenance = (): FeedProvenanceStore | undefined =>
  useContext(StaffStoresContext)?.feedProvenance;
export const useRoster = (): RosterStore => usePort('roster');
export const useTimesheet = (): TimesheetStore => usePort('timesheet');
export const useRegister = (): RegisterStore => usePort('register');
export const useChannelRead = (): ChannelReadStore => usePort('channel');
export const useBoardPresence = (): BoardPresenceService => usePort('boardPresence');
export const useBoardDemand = (): BoardDemandService => usePort('boardDemand');
export const useBoardRealtime = (): BoardRealtime => usePort('boardRealtime');
export const useChannelRealtime = (): ChannelRealtime => usePort('channelRealtime');
export const useActivityCatalog = (): ActivityCatalogService => usePort('activityCatalog');
export const useStaffingConfig = (): StaffingConfigService => usePort('staffingConfig');
export const useMetricRegistry = (): MetricRegistryService => usePort('metricRegistry');
export const useTimesheetCorrection = (): TimesheetCorrectionService => usePort('timesheetCorrection');
export const useRegisterWrite = (): RegisterService => usePort('registerWrite');
export const useChannelCuration = (): ChannelCurationService => usePort('channelCuration');
export const useShiftRequests = (): ShiftRequestService => usePort('shiftRequests');
export const useShiftRequestDecisions = (): ShiftRequestDecisionService => usePort('shiftRequestDecisions');
export const useTrustedPersons = (): TrustedPersonStore => usePort('trustedPersons');
export const useTrustedPersonService = (): TrustedPersonService => usePort('trustedPersonService');
export const useDeskList = (): DeskListService => usePort('deskList');
